import React, { useCallback, useState } from 'react';
import { Button, Card, Col, DatePicker, Descriptions, Divider, Modal, notification, Row, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import dayjs, { Dayjs } from 'dayjs';
import { ObtPartnerChannel } from '../../../__generated__/types';
import client from '../../../config/GraphQLApolloClient';
import { MUTATION_CANCEL_PARTNER_CHANNEL } from '../obtMutations';
import { formatChannelDate } from './obtChannelUtils';

// ─── Types ──────────────────────────────────────────────────────────────────────

type ObtCancelChannelModalProps = {
    open: boolean;
    selectedChannel: ObtPartnerChannel | null;
    onCancel: () => void;
    onSuccess: () => void;
};

// ─── Component ──────────────────────────────────────────────────────────────────

const ObtCancelChannelModal: React.FC<ObtCancelChannelModalProps> = ({
    open,
    selectedChannel,
    onCancel,
    onSuccess,
}) => {
    const intl = useIntl();
    const [loading, setLoading] = useState(false);
    const [newValidTo, setNewValidTo] = useState<Dayjs | null>(dayjs());

    const handleSubmit = useCallback(async () => {
        if (!selectedChannel?.id) return;

        if (!newValidTo) {
            notification.error({
                message: intl.formatMessage({ id: 'obt.cancelChannel.invalidDate', defaultMessage: 'Invalid Date' }),
                description: intl.formatMessage({ id: 'obt.cancelChannel.selectDate', defaultMessage: 'Please select a Valid To date.' }),
            });
            return;
        }

        const validFromDate = dayjs(selectedChannel.validfrom);
        if (newValidTo.isBefore(validFromDate, 'day')) {
            notification.error({
                message: intl.formatMessage({ id: 'obt.cancelChannel.dateError', defaultMessage: 'Invalid Date Range' }),
                description: intl.formatMessage({
                    id: 'obt.cancelChannel.dateError.desc',
                    defaultMessage: 'The Valid To date cannot be prior to the Valid From date.',
                }),
            });
            return;
        }

        setLoading(true);
        try {
            await client.mutate({
                mutation: MUTATION_CANCEL_PARTNER_CHANNEL,
                variables: {
                    channelId: selectedChannel.id,
                    validTo: newValidTo.format('DD.MM.YYYY'),
                    status: 'D',
                },
                fetchPolicy: 'no-cache',
            });
            notification.success({
                message: intl.formatMessage({ id: 'obt.cancelChannel.success', defaultMessage: 'Channel cancelled successfully.' }),
            });
            onSuccess();
        } catch (err) {
            console.error('Failed to cancel channel:', err);
            notification.error({
                message: intl.formatMessage({ id: 'obt.cancelChannel.error', defaultMessage: 'Failed to cancel channel.' }),
            });
        } finally {
            setLoading(false);
        }
    }, [selectedChannel, newValidTo, intl, onSuccess]);

    return (
        <Modal
            title={intl.formatMessage({ id: 'obt.cancelChannel.title', defaultMessage: 'Cancel Channel' })}
            open={open}
            width={1000}
            onCancel={onCancel}
            footer={[
                <Button key="return" onClick={onCancel}>
                    {intl.formatMessage({ id: 'obt.cancelChannel.btn.return', defaultMessage: 'Return' })}
                </Button>,
                <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
                    {intl.formatMessage({ id: 'obt.cancelChannel.btn.submit', defaultMessage: 'Submit' })}
                </Button>,
            ]}
        >
            <Spin spinning={loading} indicator={<LoadingOutlined />}>
                <Card size="small">
                    <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered={true} labelStyle={{ fontWeight: 500 }}>
                        <Descriptions.Item label={intl.formatMessage({ id: 'obt.cancelChannel.partnerno', defaultMessage: 'Partner No' })}>
                            {selectedChannel?.partnerno ?? '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label={intl.formatMessage({ id: 'obt.cancelChannel.companycode', defaultMessage: 'Company Code' })}>
                            {selectedChannel?.companycode ?? '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label={intl.formatMessage({ id: 'obt.cancelChannel.invprocess', defaultMessage: 'Inv Process' })}>
                            {selectedChannel?.invprocess ?? '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label={intl.formatMessage({ id: 'obt.cancelChannel.validfrom', defaultMessage: 'Valid From' })}>
                            {formatChannelDate(selectedChannel?.validfrom)}
                        </Descriptions.Item>
                        <Descriptions.Item label={intl.formatMessage({ id: 'obt.cancelChannel.validto', defaultMessage: 'Valid To' })}>
                            {formatChannelDate(selectedChannel?.validto)}
                        </Descriptions.Item>
                        <Descriptions.Item label={intl.formatMessage({ id: 'obt.cancelChannel.channeltype', defaultMessage: 'Channel Type' })}>
                            {selectedChannel?.channeltype ?? '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label={intl.formatMessage({ id: 'obt.cancelChannel.direction', defaultMessage: 'Direction' })}>
                            {selectedChannel?.direction ?? '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label={intl.formatMessage({ id: 'obt.cancelChannel.format', defaultMessage: 'Format' })}>
                            {selectedChannel?.format ?? '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label={intl.formatMessage({ id: 'obt.cancelChannel.provider', defaultMessage: 'Provider' })}>
                            {selectedChannel?.provider ?? '—'}
                        </Descriptions.Item>
                    </Descriptions>

                    <Divider />

                    <Row>
                        <Col span={12}>
                            <label style={{ fontWeight: 500, marginRight: 12 }}>
                                {intl.formatMessage({ id: 'obt.cancelChannel.newValidTo', defaultMessage: 'New Valid To' })}
                            </label>
                            <DatePicker
                                value={newValidTo}
                                onChange={(date) => setNewValidTo(date)}
                                format="DD.MM.YYYY"
                                placeholder={intl.formatMessage({ id: 'obt.cancelChannel.newValidTo.placeholder', defaultMessage: 'Select date' })}
                            />
                        </Col>
                    </Row>
                </Card>
            </Spin>
        </Modal>
    );
};

export default ObtCancelChannelModal;

